import { Dialog, DialogTitle, ThemeProvider, Typography } from '@mui/material';
import type { FormikHelpers } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import EligibilityFormFields from '../components/EligibilityFormFields';
import EligibilityResultsModal from '../components/EligibilityResultsModal';
import FormWrapper from 'components/common/formikForm/formWrapper';

import api from 'api';
import useEnabledCountries from 'hooks/useEnabledCountries';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import appThemeV2 from 'theme/themeV2';
import type { AssessmentResult, EligibilityFormInterface } from 'types';
import { chosenCountryName } from 'utils/countries';
import {
    eligibilityValidationSchema,
    getEligibilityInitialValues,
} from 'utils/validations/eligibility';

interface EligibilityPageProps {
    onClose: () => void;
    appId: string;
}

const EligibilityPage: React.FC<EligibilityPageProps> = ({ onClose }) => {
    const dispatch = useDispatch();
    const { translate } = useTranslate();
    const { id: candidateID } = useParams();

    const { codes, names: countryOptions } = useEnabledCountries();
    const initialCountry = chosenCountryName(codes) ?? '';

    const defaultValues = useMemo(
        () => getEligibilityInitialValues(candidateID || '', initialCountry),
        [candidateID, initialCountry]
    );

    /** Persists selections when switching to results so Back restores the form. */
    const [formValues, setFormValues] = useState<EligibilityFormInterface>(defaultValues);

    useEffect(() => {
        if (!initialCountry) return;
        setFormValues((current) =>
            current.country ? current : { ...current, country: initialCountry }
        );
    }, [initialCountry]);
    const [loading, setLoading] = useState(false);
    const [assessment, setAssessment] = useState<AssessmentResult | null>(null);

    const handleSubmit = async (
        values: EligibilityFormInterface,
        _helpers: FormikHelpers<EligibilityFormInterface>
    ) => {
        setLoading(true);
        // Keep latest form values in parent so remount after Back does not reset fields.
        setFormValues(values);
        try {
            const response = await api.single_assessment.getSingleProgram({
                country: values.country,
                program_name: values.program_name,
                candidate_id: values.candidate_id,
                enhanced_with_job_offers: values.enhanced_with_job_offers,
                enhanced_language: values.enhanced_language,
            });

            if (response.status === 201 || response.status === 200) {
                const payload = response?.data;
                const result: AssessmentResult | null = Array.isArray(payload)
                    ? payload[0]
                    : payload;
                if (result) {
                    setAssessment(result);
                } else {
                    dispatch(
                        setAlert({
                            message: translate('noAssessmentResult'),
                            type: 'error',
                        })
                    );
                }
            } else {
                dispatch(
                    setAlert({
                        message: translate('eligibilityCheckFailed'),
                        type: 'error',
                    })
                );
            }
        } catch {
            dispatch(
                setAlert({
                    message: translate('eligibilityCheckError'),
                    type: 'error',
                })
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <ThemeProvider theme={appThemeV2}>
            {assessment ? (
                <EligibilityResultsModal
                    assessment={assessment}
                    onClose={() => {
                        setAssessment(null);
                        onClose();
                    }}
                    onBack={() => setAssessment(null)}
                />
            ) : (
                <Dialog open fullWidth maxWidth="sm" scroll="paper">
                    <DialogTitle>
                        {translate('checkEligibility')}
                        <Typography variant="body2" color="text.secondary">
                            {translate('checkEligibilitySubtitle')}
                        </Typography>
                    </DialogTitle>

                    <FormWrapper<EligibilityFormInterface>
                        enableReinitialize
                        initialValues={formValues}
                        validationSchema={eligibilityValidationSchema}
                        onSubmit={handleSubmit}
                    >
                        <EligibilityFormFields
                            countryOptions={countryOptions}
                            loading={loading}
                            onClose={onClose}
                        />
                    </FormWrapper>
                </Dialog>
            )}
        </ThemeProvider>
    );
};

export default EligibilityPage;
